// ChoiceTiles — ICON TILES for a choice: single (the asset wizard's category
// grid + site/depot choice; door-decision tiles) or MULTIPLE (`multiple` — the
// files library's "what it shows" picker: the site's assets as squares, the
// topics as chips), on native inputs either way. Extracted per an architect ruling (the asset register build): the judged stories drew these
// as aria-pressed buttons; the primitive gives the choice REAL radio semantics
// instead — one native <input type="radio"> per tile (`sr-only peer`, the
// Radio.tsx technique), so arrow-key roving + SR announcement come free, across
// visual sections too (shared `name`). Options are DATA, never JSX (the
// selection-family rule); groups render presentational section headers inside
// the ONE radiogroup.

import { useId, useRef, type ReactNode } from 'react';
import type { Icon as IconType } from './icons.js';

export interface ChoiceTileOption {
  value: string;
  label: ReactNode;
  icon?: IconType;
  /** Trailing adornment (e.g. a "Coming soon" Badge on a disabled tile). */
  trailing?: ReactNode;
  disabled?: boolean;
}

export interface ChoiceTileGroup {
  label: string;
  options: ChoiceTileOption[];
}

export type ChoiceTilesOptions = ChoiceTileOption[] | ChoiceTileGroup[];

interface ChoiceTilesBase {
  options: ChoiceTilesOptions;
  /** Shared input name (one roving radio group); defaults to a generated id. */
  name?: string;
  /** Accessible name for the group (WCAG 4.1.2). */
  ariaLabel?: string;
  /** Element id of a visible group label, for aria-labelledby. */
  labelledBy?: string;
  /** Tile scale: `sm` (CHIPS — a wrapped row of pills, icon beside the word;
   *  the grid classes do not apply) · `md` (dense grids — the category grid) ·
   *  `lg` (a short row of big choices — the location kind pair). */
  size?: 'sm' | 'md' | 'lg';
  /** Tile geometry. `horizontal` (default): icon beside the label — decision
   *  tiles, short rows. `vertical`: icon top-center, label centered beneath —
   *  the category-picker grid, where labels wrap at arbitrary points and only
   *  centered symmetry keeps mixed 1–3-line rows reading uniform. */
  orientation?: 'horizontal' | 'vertical';
  /** Grid column classes for each options grid (layout is the caller's). */
  gridClassName?: string;
  className?: string;
}
/** One choice — native radios, one roving group. */
interface ChoiceTilesSingle extends ChoiceTilesBase {
  multiple?: false;
  value: string | null;
  onChange: (value: string) => void;
  /** The CHOSEN tile pressed again (a native radio fires no change for it) — for a host where pressing the current
   *  choice means "keep it" (a create wizard's kind grid re-opened by "Change"). Never fired for another tile. */
  onReselect?: (value: string) => void;
  /** The arrow keys walk the tiles WITHOUT choosing; a press (a click · Space) chooses — for a grid whose choice moves
   *  the page on (a create wizard's kind grid), where a native radio's choice-per-arrow would leave at the first step. */
  chooseOnPress?: boolean;
}
/** Several choices — native checkboxes; `value` is the checked set. */
interface ChoiceTilesMultiple extends ChoiceTilesBase {
  multiple: true;
  value: string[];
  onChange: (values: string[]) => void;
}
export type ChoiceTilesProps = ChoiceTilesSingle | ChoiceTilesMultiple;

const isGrouped = (options: ChoiceTilesOptions): options is ChoiceTileGroup[] =>
  options.length > 0 && 'options' in (options[0] as ChoiceTileGroup);

export function ChoiceTiles(props: ChoiceTilesProps) {
  const { options, name: nameProp, ariaLabel, labelledBy, size = 'md', orientation = 'horizontal', gridClassName = 'grid-cols-2 sm:grid-cols-3', className = '' } = props;
  const reactId = useId();
  const name = nameProp ?? reactId;
  const chip = size === 'sm';
  const iconSize = chip ? 16 : orientation === 'vertical' ? 24 : size === 'lg' ? 26 : 22;
  const vertical = orientation === 'vertical' && !chip;
  const isSelected = (v: string) => (props.multiple ? props.value.includes(v) : props.value === v);
  // An arrow key's walk (keydown → the native check → keyup): the controlled radio reverts, focus stays where it went.
  const walking = useRef(false);
  const walkable = !props.multiple && props.chooseOnPress === true;
  const onReselect = props.multiple ? undefined : props.onReselect;
  const pick = (v: string) => {
    if (walking.current) return;
    if (props.multiple) props.onChange(props.value.includes(v) ? props.value.filter((x) => x !== v) : [...props.value, v]);
    else props.onChange(v);
  };

  const tile = (opt: ChoiceTileOption) => {
    const selected = isSelected(opt.value);
    const Icon = opt.icon;
    return (
      <label
        key={opt.value}
        className={opt.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        // A pointer press is never a walk — a keyup lost to a window switch must not swallow the next click
        onPointerDown={walkable ? () => void (walking.current = false) : undefined}
      >
        <input
          type={props.multiple ? 'checkbox' : 'radio'}
          name={name}
          value={opt.value}
          checked={selected}
          disabled={opt.disabled}
          onChange={() => pick(opt.value)}
          // A click on the radio already checked changes nothing natively — the host may still hear it. Chromium also
          // dispatches a click on the tile an arrow lands on, the chosen one included: a walk back onto it is not a press.
          onClick={selected && onReselect ? () => void (walking.current || onReselect(opt.value)) : undefined}
          onKeyDown={
            walkable || (selected && onReselect)
              ? (e) => {
                  if (walkable) walking.current = e.key.startsWith('Arrow');
                  // Space on the chosen tile: Chromium dispatches no click for a checked radio — heard here, once
                  if (selected && onReselect && e.key === ' ') {
                    e.preventDefault();
                    onReselect(opt.value);
                  }
                }
              : undefined
          }
          onKeyUp={walkable ? () => void (walking.current = false) : undefined}
          className="peer sr-only"
        />
        {/* h-full: the grid stretches the (blockified) label, and the tile must
            fill it — otherwise tiles in one row render unequal heights the
            moment a label wraps. Horizontal: icon rides the FIRST text line
            (items-start + mt-px), every tile's content starts on one shared
            line. Vertical: icon top-center, label centered beneath — the only
            geometry where 1–3-line labels read uniform in one row. */}
        <span
          className={`flex h-full border text-sm transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--color-primary-ring)] ${
            chip
              ? 'items-center gap-1.5 rounded-full px-3 py-1.5'
              : vertical
                ? 'flex-col items-center gap-1.5 rounded-lg p-3 text-center'
                : `items-start rounded-lg text-start ${size === 'lg' ? 'gap-3 p-3.5' : 'gap-2.5 p-3'}`
          } ${
            selected
              ? 'border-[var(--color-primary-display)] bg-[var(--color-primary-light)] font-medium text-[var(--color-text-primary)]'
              : opt.disabled
                ? 'border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-disabled)]'
                : 'border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
          }`}
        >
          {Icon && (
            <Icon
              size={iconSize}
              aria-hidden
              className={`shrink-0 ${vertical ? '' : 'mt-px'} ${opt.disabled ? 'text-[var(--color-text-disabled)]' : 'text-[var(--color-text-secondary)]'}`}
            />
          )}
          {opt.label}
          {opt.trailing && <span className={vertical ? 'pt-1' : chip ? 'ps-1' : 'ms-auto ps-2'}>{opt.trailing}</span>}
        </span>
      </label>
    );
  };

  // Chips wrap in a row; tiles sit on the caller's grid.
  const lay = chip ? 'flex flex-wrap gap-2' : `grid gap-2 ${gridClassName}`;
  return (
    <div
      role={props.multiple ? 'group' : 'radiogroup'}
      aria-label={labelledBy ? undefined : ariaLabel}
      aria-labelledby={labelledBy}
      className={className}
    >
      {isGrouped(options) ? (
        // gap-6/mt-2: the house SECTION rhythm (24px between groups, 8px
        // header→grid) — gap-4 read cramped at the wizard eyeball.
        <div className="flex flex-col gap-6">
          {options.map((group) => (
            <div key={group.label}>
              <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                {group.label}
              </div>
              <div className={`mt-2 ${lay}`}>{group.options.map(tile)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className={lay}>{options.map(tile)}</div>
      )}
    </div>
  );
}
